/* Sharrow flow comparison.
   Two modes:
   - Illustration: the throttle drives a stylized comparison.
   - Test data: a Sharrow Marine performance report (assets/sharrow-flow-data.js) drives each propeller's
     boat speed at the selected engine RPM. From that we calculate, live:
       prop RPM = engine RPM / gear ratio
       theoretical (pitch) speed = prop RPM x pitch(in) / 1056   [MPH]
       slip = 1 - actual / theoretical
       tip cavitation number  sigma = (p_atm + rho*g*h - p_vapor) / (0.5*rho*(U_tip^2 + Va^2))
       blade-tip angle of attack alpha = pitch angle at tip - advance angle at tip
       tip-vortex cavitation index = sigma_i / sigma, with sigma_i = K*alpha^2 (loading-driven inception)
     and the water flow, vapor, bubbles, vibration and sound follow those numbers.
   The water rendering itself is a stylized illustration, not a CFD simulation. */
(function(){
  const reduceMotion=window.matchMedia('(prefers-reduced-motion: reduce)');
  const TAU=Math.PI*2;
  const clamp=(v,a,b)=>Math.max(a,Math.min(b,v));
  const smooth=(a,b,x)=>{const t=clamp((x-a)/(b-a),0,1);return t*t*(3-2*t);};

  /* ---------- physics ---------- */
  const PHYS={rho:1025,g:9.81,patm:101325,pv:2339,tipDepth:0.45,wake:0.05,K:11};
  function interp(pts,r){
    if(r<=pts[0][0]) return pts[0][1]*Math.max(0,r)/pts[0][0];
    for(let i=1;i<pts.length;i++){
      if(r<=pts[i][0]){const a=pts[i-1],b=pts[i];return a[1]+(b[1]-a[1])*(r-a[0])/(b[0]-a[0]);}
    }
    return pts[pts.length-1][1];
  }
  function propPhysics(rep,prop,rpm){
    const maxR=prop.pts[prop.pts.length-1][0];
    const r=Math.min(rpm,maxR);
    let v=interp(prop.pts,r);
    if(rep.units==='kn') v*=1.15078;
    const n=r/rep.gr;
    const vth=n*prop.p/1056;
    const slip=vth>0?clamp(1-v/vth,0,0.95):0;
    const D=prop.d*0.0254,P=prop.p*0.0254,nps=n/60;
    const Va=v*0.44704*(1-PHYS.wake),Ut=Math.PI*nps*D;
    const q=0.5*PHYS.rho*(Ut*Ut+Va*Va);
    const sigma=q>0?(PHYS.patm+PHYS.rho*PHYS.g*PHYS.tipDepth-PHYS.pv)/q:99;
    const alpha=Math.max(0,Math.atan(P/(Math.PI*D))-Math.atan2(Va,Ut));
    const index=PHYS.K*alpha*alpha/sigma;
    return {rpm:r,wot:rpm>=maxR,maxR,v,vth,n,slip,sigma,alpha,index};
  }
  function cavLevel(idx){return idx<0.7?0:idx<1?1:idx<1.8?2:idx<2.6?3:4;}
  const CAV_NAMES=['None','Inception','Moderate','Heavy','Severe'];

  /* ---------- lane setup ---------- */
  function makeLane(el,kind){
    const box=el.querySelector('.sflow-canvas-box');
    const bgC=el.querySelector('.sflow-bg');
    const trails=el.querySelector('.sflow-trails');
    const propC=el.querySelector('.sflow-prop');
    const waveC=el.querySelector('.sflow-wave');
    const meters={};
    el.querySelectorAll('[data-sflow-meter]').forEach(m=>{meters[m.dataset.sflowMeter]=m;});
    const lane={el,kind,box,bgC,trails,propC,bctx:bgC.getContext('2d'),tctx:trails.getContext('2d'),pctx:propC.getContext('2d'),
      meters,stat:el.querySelector('[data-sflow-lanestat]'),w:0,h:0,xp:0,cy:0,R:0,parts:[],bubbles:[],specks:[],rays:[],rings:[],
      rot:Math.random()*TAU,t:Math.random()*50,bubbleAcc:0,flow:0,blades:3,
      waveC,wctx:waveC?waveC.getContext('2d'):null,ww:0,wh:0,wave:[],wph:0,
      S:{rot:0.4,flow:0.4,cav:0.2,vib:0.3,turb:0.4}};
    lane.size=function(){
      const dpr=Math.min(2,window.devicePixelRatio||1);
      const w=box.clientWidth,h=box.clientHeight;
      if(!w||!h) return false;
      lane.w=w;lane.h=h;
      [bgC,trails,propC].forEach(c=>{c.width=Math.round(w*dpr);c.height=Math.round(h*dpr);});
      [lane.bctx,lane.tctx,lane.pctx].forEach(c=>c.setTransform(dpr,0,0,dpr,0,0));
      lane.cy=h*0.53;lane.R=h*0.29;
      lane.xp=w<560?Math.max(lane.R*2.6,w*0.3):Math.max(lane.R*2.85,w*0.25);
      lane.tctx.clearRect(0,0,w,h);
      const n=Math.round(clamp(w*0.42,130,480));
      lane.parts=[];for(let i=0;i<n;i++) lane.parts.push(spawn(lane,true,i/n));
      lane.specks=[];for(let i=0;i<Math.round(clamp(w*0.05,18,60));i++) lane.specks.push({x:Math.random()*w,y:Math.random()*h,r:0.4+Math.random()*1.1,a:0.12+Math.random()*0.25,v:0.2+Math.random()*0.5});
      lane.rays=[];for(let i=0;i<6;i++) lane.rays.push({x:Math.random()*w*1.2-w*0.1,wd:20+Math.random()*70,sk:-0.25+Math.random()*0.15,ph:Math.random()*TAU,sp:0.2+Math.random()*0.4});
      lane.bubbles=[];lane.rings=[];
      if(waveC){
        const ww=waveC.clientWidth,wh=waveC.clientHeight;
        if(ww&&wh){lane.ww=ww;lane.wh=wh;waveC.width=Math.round(ww*dpr);waveC.height=Math.round(wh*dpr);lane.wctx.setTransform(dpr,0,0,dpr,0,0);lane.wave=new Array(Math.ceil(ww/2)).fill(0);}
      }
      return true;
    };
    return lane;
  }

  function spawn(l,initial,frac){
    const R=l.R;
    let d;
    if(l.kind==='conv' && Math.random()<0.3){
      d=(Math.random()<0.5?-1:1)*R*(0.78+Math.random()*0.26);
    }else{
      const lo=R*0.26,hi=l.h*0.52;
      d=(Math.random()<0.5?-1:1)*(lo+Math.random()*(hi-lo));
    }
    return {x:initial?(frac*1.15-0.1)*l.w:-Math.random()*40,y0:l.cy+d,s:Math.random(),p:Math.random()*TAU,px:null,py:null};
  }

  /* ---------- background: water, light shafts, particulates ---------- */
  function drawBackground(l,dt){
    const {bctx:c,w,h}=l;
    const g=c.createLinearGradient(0,0,0,h);
    g.addColorStop(0,'#0e4a6e');g.addColorStop(0.28,'#0a3655');g.addColorStop(0.7,'#05192c');g.addColorStop(1,'#020a14');
    c.globalCompositeOperation='source-over';
    c.fillStyle=g;c.fillRect(0,0,w,h);
    const sg=c.createLinearGradient(0,0,0,h*0.12);
    sg.addColorStop(0,'rgba(170,225,255,0.16)');sg.addColorStop(1,'rgba(170,225,255,0)');
    c.fillStyle=sg;c.fillRect(0,0,w,h*0.12);
    c.globalCompositeOperation='lighter';
    for(const r of l.rays){
      r.ph+=dt*r.sp;
      const a=0.035+0.03*Math.sin(r.ph);
      const x0=r.x+Math.sin(l.t*0.15+r.ph)*8,x1=x0+r.sk*h*2.2;
      const rg=c.createLinearGradient(0,0,0,h*0.95);
      rg.addColorStop(0,'rgba(190,235,255,'+a.toFixed(3)+')');rg.addColorStop(1,'rgba(190,235,255,0)');
      c.fillStyle=rg;
      c.beginPath();c.moveTo(x0,0);c.lineTo(x0+r.wd,0);c.lineTo(x1+r.wd*1.8,h);c.lineTo(x1,h);c.closePath();c.fill();
    }
    for(const s of l.specks){
      s.x+=l.flow*s.v*dt*0.35;s.y+=Math.sin(l.t*0.7+s.x*0.01)*0.06;
      if(s.x>w+4){s.x=-4;s.y=Math.random()*h;}
      c.fillStyle='rgba(200,235,255,'+s.a.toFixed(2)+')';
      c.beginPath();c.arc(s.x,s.y,s.r,0,TAU);c.fill();
    }
    c.globalCompositeOperation='source-over';
    const vg=c.createRadialGradient(w*0.45,h*0.5,h*0.2,w*0.5,h*0.5,Math.max(w,h)*0.75);
    vg.addColorStop(0,'rgba(0,0,0,0)');vg.addColorStop(1,'rgba(0,6,14,0.55)');
    c.fillStyle=vg;c.fillRect(0,0,w,h);
  }

  /* ---------- water flow streaks ---------- */
  function stepFlow(l,dt){
    const {w,h,xp,cy,R,tctx,S}=l;
    l.t+=dt;
    const t=l.t;
    const u0=w*(0.06+0.40*S.flow);l.flow=u0;
    const omega=TAU*(0.8+5.5*S.rot);l.omega=omega;
    l.rot=(l.rot+omega*dt*0.35)%TAU;

    tctx.globalCompositeOperation='destination-out';
    tctx.fillStyle='rgba(0,0,0,'+(0.13+0.07*S.flow).toFixed(3)+')';
    tctx.fillRect(0,0,w,h);
    tctx.globalCompositeOperation='source-over';

    const conv=l.kind==='conv';
    for(const p of l.parts){
      const d=p.y0-cy, ad=Math.abs(d);
      const inDisk=ad<R*1.06;
      const accel=inDisk?(conv?0.4:0.75)*smooth(xp-1.6*R,xp+R,p.x)*(0.4+0.6*S.rot):0;
      const f=1+accel;
      p.x+=u0*f*dt;
      let x=p.x,y,a=0.22,lw=1,col='185,225,245';
      if(p.x<xp){
        const s=smooth(xp-1.6*R,xp,p.x);
        y=cy+d*(1-(inDisk?0.1:0.035)*s);
      }else{
        const dx=p.x-xp, ramp=Math.min(1,dx/R);
        if(conv){
          const A=R*(0.03+0.17*S.turb)*ramp*(inDisk?1:0.45);
          y=cy+d*(inDisk?0.9:0.97)+A*(0.6*Math.sin(p.s*13+t*(3+5*S.turb)+dx*0.021)+0.4*Math.sin(p.s*7.3-t*(2.1+4*S.turb)+dx*0.047));
          a=inDisk?0.26:0.2;
        }else{
          const A=R*(0.015+0.06*S.turb)*ramp;
          y=cy+d*(inDisk?0.8:0.975)+A*Math.sin(p.s*11+t*2+dx*0.012);
          if(inDisk){a=0.32+0.25*S.flow;lw=1.25;col='160,230,255';}
        }
      }
      if(p.px!==null&&Math.abs(x-p.px)<40&&Math.abs(y-p.py)<40){
        tctx.strokeStyle='rgba('+col+','+a.toFixed(2)+')';tctx.lineWidth=lw;
        tctx.beginPath();tctx.moveTo(p.px,p.py);tctx.lineTo(x,y);tctx.stroke();
      }
      p.px=x;p.py=y;
      if(p.x>w+12) Object.assign(p,spawn(l,false));
    }
  }

  /* ---------- tip-vortex helix ----------
     Each blade tip sheds a vortex that is carried downstream on a helix around the shaft axis.
     Its pitch is the distance the prop advances per revolution (J x D), so a heavily slipping prop
     winds a tight spiral and an efficient one a long, open one. Side view: y = r sin(angle), depth = cos(angle). */
  const HELIX_X0=0.06;
  function helixAt(l,j,dx){
    const {xp,cy,R}=l, P=Math.max(0.35,l.J||0.8)*2*R;
    const a=l.rot+j*TAU/l.blades-TAU*dx/P;
    const r=R*(0.98-0.1*Math.min(1,dx/(1.6*R)));
    return {x:xp+HELIX_X0*R+dx,y:cy+r*Math.sin(a),z:Math.cos(a)};
  }
  const vaporLen=(l,cav)=>l.R*(0.5+2.4*cav)*cav;

  /* ---------- bubbles + pressure pulses ---------- */
  function stepBubbles(l,dt){
    const {w,xp,cy,R,S}=l, conv=l.kind==='conv', u0=l.flow;
    const scale=clamp(w/1100,0.4,1.2);
    const Lvap=vaporLen(l,S.cav);
    const rate=(conv?Math.max(0,S.cav-0.05)*150:Math.max(0,S.cav-0.1)*25)*scale;
    l.bubbleAcc+=rate*dt;
    while(l.bubbleAcc>=1){
      l.bubbleAcc-=1;
      let bx,by;
      if(conv){
        /* the vapor core breaks up into bubbles where the helix ends, so bubbles trail the spiral */
        const hp=helixAt(l,Math.floor(Math.random()*l.blades),Lvap*(0.55+Math.random()*0.6));
        bx=hp.x;by=hp.y+(Math.random()-0.5)*R*0.08;
      }else{
        const sgn=Math.random()<0.5?-1:1;
        bx=xp+R*(0.1+Math.random()*0.3);by=cy+sgn*R*(0.3+Math.random()*0.25);
      }
      l.bubbles.push({x:bx,y:by,r:0.7+Math.random()*(conv?2.4:1.4),life:0,max:1+Math.random()*1.4,vx:0.8+Math.random()*0.25,wob:Math.random()*TAU});
    }
    l.bubbles=l.bubbles.filter(b=>{
      b.life+=dt;
      if(b.life>b.max){
        if(conv&&b.r>1.2&&Math.random()<0.4&&l.rings.length<40) l.rings.push({x:b.x,y:b.y,life:0,max:0.45+Math.random()*0.3,r:R*(0.25+Math.random()*0.35)});
        return false;
      }
      b.x+=u0*b.vx*dt;b.y-=dt*(6+b.r*7);b.wob+=dt*6;
      return b.x<w+8;
    });
    l.rings=l.rings.filter(g=>{g.life+=dt;g.x+=u0*0.8*dt;return g.life<g.max;});
  }
  function drawBubbles(l){
    const c=l.pctx;
    for(const b of l.bubbles){
      const fade=Math.min(1,b.life*4)*(1-b.life/b.max);
      const bx=b.x+Math.sin(b.wob)*0.8;
      c.fillStyle='rgba(210,240,255,'+(0.10*fade).toFixed(3)+')';
      c.strokeStyle='rgba(235,248,255,'+(0.75*fade).toFixed(3)+')';c.lineWidth=0.8;
      c.beginPath();c.arc(bx,b.y,b.r,0,TAU);c.fill();c.stroke();
      if(b.r>1.4){c.fillStyle='rgba(255,255,255,'+(0.85*fade).toFixed(3)+')';c.beginPath();c.arc(bx-b.r*0.35,b.y-b.r*0.35,b.r*0.28,0,TAU);c.fill();}
    }
  }
  function drawRings(l){
    const c=l.pctx;
    for(const g of l.rings){
      const k=g.life/g.max, rr=2+g.r*k;
      c.strokeStyle='rgba(225,242,255,'+(0.35*(1-k)).toFixed(3)+')';c.lineWidth=1.2*(1-k)+0.4;
      c.beginPath();c.arc(g.x,g.y,rr,0,TAU);c.stroke();
    }
  }

  /* ---------- cavitating tip vortices: one vapor helix per blade tip ---------- */
  function drawVortices(l,front){
    if(l.kind!=='conv') return;
    const {pctx:c,R,S}=l;
    const vap=S.cav;
    if(vap<=0.02) return;
    const Lvap=vaporLen(l,vap), Ltrail=Lvap+R*(0.8+1.2*vap);
    const step=Math.max(2,R/26);
    c.lineCap='round';c.lineJoin='round';
    for(let j=0;j<l.blades;j++){
      let prev=helixAt(l,j,0);
      for(let dx=step;dx<=Ltrail;dx+=step){
        const p=helixAt(l,j,dx);
        const zm=(p.z+prev.z)/2;
        if((zm>=0)===front){
          const depth=zm>=0?0.55+0.45*zm:0.28;
          if(dx<=Lvap){
            /* cavitating core: thick white rope, beaded, thinning toward its end */
            const f=dx/Lvap;
            const bead=0.75+0.25*Math.sin(dx*0.55+l.t*9+j);
            const wdt=Math.max(0.9,R*0.04*(0.5+0.8*vap)*(1-0.45*f)*bead);
            const fade=(1-Math.pow(f,2.2))*Math.min(1,vap*1.8);
            c.strokeStyle='rgba(195,228,255,'+(0.14*fade*depth).toFixed(3)+')';c.lineWidth=wdt*3.4;
            c.beginPath();c.moveTo(prev.x,prev.y);c.lineTo(p.x,p.y);c.stroke();
            c.strokeStyle='rgba(246,251,255,'+(0.88*fade*depth).toFixed(3)+')';c.lineWidth=wdt;
            c.beginPath();c.moveTo(prev.x,prev.y);c.lineTo(p.x,p.y);c.stroke();
          }else{
            /* the vortex keeps spiralling after the vapor collapses: faint swirl line */
            const f=(dx-Lvap)/(Ltrail-Lvap);
            c.strokeStyle='rgba(205,232,250,'+(0.28*(1-f)*depth*Math.min(1,vap*2)).toFixed(3)+')';c.lineWidth=1;
            c.beginPath();c.moveTo(prev.x,prev.y);c.lineTo(p.x,p.y);c.stroke();
          }
        }
        prev=p;
      }
    }
    if(front){
      const {xp,cy}=l, hubL=R*(0.4+1.6*vap)*vap;
      c.beginPath();
      for(let dx=0;dx<=hubL;dx+=3){const y=cy+Math.sin((dx-l.flow*l.t)*0.09)*R*0.025*(dx/hubL);dx===0?c.moveTo(xp+0.55*R+dx,y):c.lineTo(xp+0.55*R+dx,y);}
      c.strokeStyle='rgba(240,250,255,'+(0.45*vap).toFixed(3)+')';c.lineWidth=Math.max(0.8,R*0.02);c.stroke();
    }
  }


  /* ---------- real geometry ----------
     Pre-rendered from the CAD models (no 3D data is shipped): the Yamaha gearcase, a Mercury ECO 16 x 17 RH
     (3-blade) and a SHARROW MX 15.5 (3-loop), each turning right-hand. World units are mm with the origin on the
     shaft axis at the aft face of the torpedo; X runs aft, Y up. 24 frames cover one blade passage (120 deg).
     The lanes show the STARBOARD side so the boat travels left to right like the overhead race: the images are
     rendered from starboard and stored mirrored, and the lane canvases are mirrored back with CSS. On screen the
     blade angle is phi = -rot (right-hand from starboard), so tip height is cy + R sin(rot). */
  const GEO={"k":1.2,"N":24,"cols":6,"lu":{"l":-380,"t":470,"w":640,"h":710},"conv":{"l":-30,"t":215,"w":320,"h":430,"fw":384,"fh":516,"th0":5.95433607987739,"view":"starboard","r":200.8227483787834,"tipX":152.38528442382812,"blades":3},"sharrow":{"l":-30,"t":215,"w":320,"h":430,"fw":384,"fh":516,"th0":2.025250202652378,"view":"starboard","r":196.8502233833687,"tipX":232.9366455078125,"blades":3}};
  GEO.Xc=140;GEO.img={};
  function loadGeo(root){
    [['lu','sflowGeoLu'],['conv','sflowGeoConv'],['sharrow','sflowGeoSharrow']].forEach(([k,attr])=>{
      const url=root.dataset[attr];if(!url||GEO.img[k]) return;
      const im=new Image();im.decoding='async';im.onload=()=>{GEO.img[k]=im;if(root.sflowRedraw) root.sflowRedraw();};im.src=url;
    });
  }
  const geoScale=l=>l.R/200;
  function drawGeoLU(l){
    const im=GEO.img.lu;if(!im) return false;
    const s=geoScale(l),m=GEO.lu;
    l.pctx.drawImage(im,l.xp+(m.l-GEO.Xc)*s,l.cy-m.t*s,m.w*s,m.h*s);
    return true;
  }
  function drawGeoProp(l){
    const m=GEO[l.kind],im=GEO.img[l.kind];
    if(!im||!m||l.blades!==m.blades) return false;
    const c=l.pctx,s=geoScale(l),per=TAU/m.blades,step=per/GEO.N;
    const base=(((l.rot+m.th0)%per)+per)%per/step;
    const frame=(k,a)=>{const i=((Math.round(k)%GEO.N)+GEO.N)%GEO.N;
      c.globalAlpha=a;c.drawImage(im,(i%GEO.cols)*m.fw,Math.floor(i/GEO.cols)*m.fh,m.fw,m.fh,l.xp+(m.l-GEO.Xc)*s,l.cy-m.t*s,m.w*s,m.h*s);};
    if(l.S.rot>0.25){const g=Math.max(1,Math.round(1.6*l.S.rot*GEO.N/12));frame(base-2*g,0.18);frame(base-g,0.32);}
    frame(base,1);c.globalAlpha=1;
    return true;
  }

  /* ---------- hardware: lower unit, hub, blades ---------- */
  function paintGrad(c,y0,y1){
    const g=c.createLinearGradient(0,y0,0,y1);
    g.addColorStop(0,'#4a5562');g.addColorStop(0.18,'#2d3640');g.addColorStop(0.6,'#171d24');g.addColorStop(1,'#0a0e13');
    return g;
  }
  /* Outboard / sterndrive gearcase in side view (water flows left to right, prop aft):
     streamlined leg with a raked leading edge, anti-ventilation plate over the prop, torpedo housing
     with a rounded nose, water-intake slots, and a swept skeg under the torpedo ahead of the prop. */
  function drawLowerUnit(l){
    const {pctx:c,xp,cy,R}=l;
    const hr=0.25*R;                         /* torpedo radius */
    const nose=xp-2.55*R, tail=xp-0.22*R;    /* torpedo nose / aft end at the prop hub */
    const plateY=cy-1.13*R, plateT=0.07*R;   /* anti-ventilation plate just above the blade tips */
    const legLE=y=>xp-1.95*R-(cy-y)*0.12;    /* leading edge rakes forward going up */
    const legTE=y=>xp-0.62*R-(cy-y)*0.05;
    const paint=(x0,x1)=>{const g=c.createLinearGradient(x0,0,x1,0);
      g.addColorStop(0,'#10151a');g.addColorStop(0.18,'#3a4652');g.addColorStop(0.42,'#2a333d');g.addColorStop(0.8,'#161c22');g.addColorStop(1,'#0b0f13');return g;};
    /* leg (strut), from the canvas top down into the torpedo */
    const yTop=-6, yBot=cy-hr*0.5;
    c.fillStyle=paint(legLE(yBot),legTE(yBot));
    c.beginPath();
    c.moveTo(legLE(yTop),yTop);
    c.bezierCurveTo(legLE(yTop)-0.02*R,(yTop+yBot)/2,legLE(yBot)-0.05*R,yBot-0.25*R,legLE(yBot)+0.1*R,yBot);
    c.lineTo(legTE(yBot),yBot);
    c.lineTo(legTE(yTop),yTop);
    c.closePath();c.fill();
    /* leading-edge highlight */
    c.strokeStyle='rgba(255,255,255,0.12)';c.lineWidth=1.2;
    c.beginPath();c.moveTo(legLE(yTop)+0.05*R,yTop);c.lineTo(legLE(plateY)+0.06*R,plateY);c.stroke();
    /* torpedo housing */
    const tg=c.createLinearGradient(0,cy-hr,0,cy+hr);
    tg.addColorStop(0,'#55616e');tg.addColorStop(0.22,'#323c46');tg.addColorStop(0.6,'#171d24');tg.addColorStop(1,'#0a0e12');
    c.fillStyle=tg;
    c.beginPath();
    c.moveTo(nose+hr*1.2,cy-hr);
    c.bezierCurveTo(xp-1.5*R,cy-hr*1.04,xp-0.7*R,cy-hr*0.98,tail,cy-0.19*R);
    c.lineTo(tail,cy+0.19*R);
    c.bezierCurveTo(xp-0.7*R,cy+hr*0.98,xp-1.5*R,cy+hr*1.04,nose+hr*1.2,cy+hr);
    c.bezierCurveTo(nose+hr*0.2,cy+hr,nose,cy+hr*0.45,nose,cy);
    c.bezierCurveTo(nose,cy-hr*0.45,nose+hr*0.2,cy-hr,nose+hr*1.2,cy-hr);
    c.closePath();c.fill();
    c.strokeStyle='rgba(255,255,255,0.16)';c.lineWidth=1.1;
    c.beginPath();c.moveTo(nose+hr*0.7,cy-hr*0.62);c.bezierCurveTo(xp-1.6*R,cy-hr*0.72,xp-0.8*R,cy-hr*0.66,tail-2,cy-0.13*R);c.stroke();
    /* water-intake slots on the nose */
    c.fillStyle='rgba(0,0,0,0.6)';
    for(let i=0;i<4;i++){const ix=nose+hr*1.05+i*0.09*R;c.beginPath();c.ellipse(ix,cy-hr*0.05,0.022*R,hr*0.42,0,0,TAU);c.fill();}
    /* skeg: swept leading edge, trailing edge just ahead of the prop */
    const sg=c.createLinearGradient(0,cy,0,cy+1.3*R);
    sg.addColorStop(0,'#2a333d');sg.addColorStop(0.5,'#161c22');sg.addColorStop(1,'#0b0f13');
    c.fillStyle=sg;
    c.beginPath();
    c.moveTo(xp-1.55*R,cy+hr*0.85);
    c.bezierCurveTo(xp-1.25*R,cy+0.6*R,xp-0.95*R,cy+1.05*R,xp-0.62*R,cy+1.26*R);
    c.quadraticCurveTo(xp-0.5*R,cy+1.3*R,xp-0.5*R,cy+1.18*R);
    c.lineTo(xp-0.6*R,cy+hr*0.8);
    c.closePath();c.fill();
    c.strokeStyle='rgba(255,255,255,0.08)';c.lineWidth=1;
    c.beginPath();c.moveTo(xp-1.5*R,cy+hr*0.95);c.bezierCurveTo(xp-1.22*R,cy+0.62*R,xp-0.95*R,cy+1.03*R,xp-0.64*R,cy+1.22*R);c.stroke();
    /* trim tab under the plate, behind the leg */
    c.fillStyle='#1c232a';
    c.beginPath();c.moveTo(legTE(plateY)-0.02*R,plateY+plateT*0.4);c.lineTo(xp-0.3*R,plateY+plateT*0.4);c.lineTo(xp-0.38*R,plateY+0.2*R);c.lineTo(legTE(plateY)+0.02*R,plateY+0.16*R);c.closePath();c.fill();
    /* anti-ventilation plate, extending aft over the prop with a slight downturn */
    const pg=c.createLinearGradient(0,plateY-plateT,0,plateY+plateT);
    pg.addColorStop(0,'#6a7682');pg.addColorStop(0.45,'#2c353f');pg.addColorStop(1,'#0e1318');
    c.fillStyle=pg;
    c.beginPath();
    c.moveTo(legLE(plateY)-0.1*R,plateY);
    c.quadraticCurveTo(legLE(plateY)-0.12*R,plateY-plateT,legLE(plateY)+0.05*R,plateY-plateT);
    c.lineTo(xp+0.3*R,plateY-plateT*0.85);
    c.quadraticCurveTo(xp+0.48*R,plateY-plateT*0.6,xp+0.5*R,plateY+plateT*0.5);
    c.lineTo(xp+0.42*R,plateY+plateT*0.9);
    c.lineTo(legLE(plateY)+0.05*R,plateY+plateT);
    c.quadraticCurveTo(legLE(plateY)-0.12*R,plateY+plateT,legLE(plateY)-0.1*R,plateY);
    c.closePath();c.fill();
    c.strokeStyle='rgba(255,255,255,0.22)';c.lineWidth=1;
    c.beginPath();c.moveTo(legLE(plateY)+0.05*R,plateY-plateT*0.8);c.lineTo(xp+0.3*R,plateY-plateT*0.7);c.stroke();
  }
  function metal(c,xa,xb,z,alpha){
    const g=c.createLinearGradient(xa,0,xb,0);
    const k=z>0?1:0.55;
    const s=v=>Math.round(v*k);
    g.addColorStop(0,'rgba('+s(205)+','+s(215)+','+s(225)+','+alpha+')');
    g.addColorStop(0.35,'rgba('+s(245)+','+s(249)+','+s(252)+','+alpha+')');
    g.addColorStop(0.65,'rgba('+s(150)+','+s(165)+','+s(180)+','+alpha+')');
    g.addColorStop(1,'rgba('+s(85)+','+s(100)+','+s(115)+','+alpha+')');
    return g;
  }
  function drawConvBlade(l,th,alpha){
    const {pctx:c,xp,cy,R}=l;
    const e=Math.sin(th),z=Math.cos(th),face=0.4+0.6*Math.abs(z);
    const lead=[],trail=[];
    for(let i=0;i<=10;i++){
      const s=0.18+0.82*i/10;
      const y=cy+e*R*s;
      const wl=(0.07*R+0.17*R*Math.sin(Math.PI*Math.min(1,s*1.02)))*face;
      const wt=(0.08*R+0.14*R*Math.sin(Math.PI*Math.min(1,s*0.98)))*face;
      const skew=0.07*R*s*s;
      lead.push([xp-wl+skew,y]);trail.push([xp+0.06*R+wt+skew*0.5,y]);
    }
    c.fillStyle=metal(c,xp-0.25*R,xp+0.3*R,z,alpha.toFixed(3));
    c.beginPath();c.moveTo(lead[0][0],lead[0][1]);
    for(let i=1;i<lead.length;i++) c.lineTo(lead[i][0],lead[i][1]);
    const tip=cy+e*R*1.01;
    c.quadraticCurveTo(xp+0.05*R,tip,trail[trail.length-1][0],trail[trail.length-1][1]);
    for(let i=trail.length-2;i>=0;i--) c.lineTo(trail[i][0],trail[i][1]);
    c.closePath();c.fill();
    if(alpha>0.5){
      c.strokeStyle='rgba(10,18,26,0.45)';c.lineWidth=0.8;c.stroke();
      if(z>0){c.strokeStyle='rgba(255,255,255,'+(0.55*z).toFixed(2)+')';c.lineWidth=1.2;
        c.beginPath();c.moveTo(lead[2][0]+1,lead[2][1]);for(let i=3;i<lead.length;i++) c.lineTo(lead[i][0]+1,lead[i][1]);c.stroke();}
    }
  }
  function loopPath(c,xp,cy,R,e,z){
    const sg=e<0?-1:1, root=0.17*R, ow=0.55+0.45*Math.abs(z);
    c.beginPath();
    c.moveTo(xp-0.15*R,cy+sg*root);
    c.bezierCurveTo(xp-0.42*R*ow,cy+e*R*0.55,xp-0.26*R*ow,cy+e*R*1.03,xp+0.05*R,cy+e*R);
    c.bezierCurveTo(xp+0.36*R*ow,cy+e*R*0.97,xp+0.46*R*ow,cy+e*R*0.5,xp+0.27*R,cy+sg*root);
  }
  function drawLoopBlade(l,th,alpha){
    const {pctx:c,xp,cy,R}=l;
    const e=Math.sin(th),z=Math.cos(th);
    const tw=Math.max(2.4,0.08*R);
    c.lineJoin='round';c.lineCap='round';
    loopPath(c,xp,cy,R,e,z);
    c.fillStyle='rgba(150,205,235,'+(0.05*alpha).toFixed(3)+')';c.fill();
    c.strokeStyle='rgba(8,14,22,'+(0.6*alpha).toFixed(3)+')';c.lineWidth=tw*1.35;c.stroke();
    c.strokeStyle=metal(c,xp-0.45*R,xp+0.5*R,z,alpha.toFixed(3));c.lineWidth=tw;c.stroke();
    if(alpha>0.5&&z>0){
      c.save();c.translate(0,-tw*0.22*Math.sign(e||1));
      loopPath(c,xp,cy,R,e,z);
      c.strokeStyle='rgba(255,255,255,'+(0.5*z*alpha).toFixed(3)+')';c.lineWidth=tw*0.22;c.stroke();
      c.restore();
    }
  }
  function drawHub(l){
    const {pctx:c,xp,cy,R}=l;
    const hg=c.createLinearGradient(0,cy-0.2*R,0,cy+0.2*R);
    hg.addColorStop(0,'#dfe7ee');hg.addColorStop(0.3,'#f6f9fb');hg.addColorStop(0.6,'#8394a5');hg.addColorStop(1,'#3a4856');
    c.fillStyle=hg;
    c.beginPath();
    c.moveTo(xp-0.19*R,cy-0.19*R);c.lineTo(xp+0.27*R,cy-0.17*R);
    c.quadraticCurveTo(xp+0.56*R,cy,xp+0.27*R,cy+0.17*R);
    c.lineTo(xp-0.19*R,cy+0.19*R);c.closePath();c.fill();
    c.strokeStyle='rgba(10,18,26,0.4)';c.lineWidth=0.8;c.stroke();
    c.fillStyle='rgba(20,28,36,0.6)';c.fillRect(xp-0.21*R,cy-0.2*R,0.04*R,0.4*R);
  }
  function drawHardware(l){
    const {pctx:c,w,h,S}=l;
    c.clearRect(0,0,w,h);
    if(l.kind==='sharrow'){
      const {xp,cy,R}=l, len=Math.min(w-xp,R*(3+5*S.flow));
      c.save();c.translate(xp+len*0.42,cy);c.scale(len*0.55/R,0.95);
      const jg=c.createRadialGradient(0,0,0,0,0,R);
      jg.addColorStop(0,'rgba(142,227,255,'+(0.10+0.12*S.flow).toFixed(3)+')');jg.addColorStop(0.55,'rgba(142,227,255,'+(0.05+0.06*S.flow).toFixed(3)+')');jg.addColorStop(1,'rgba(142,227,255,0)');
      c.fillStyle=jg;c.beginPath();c.arc(0,0,R,0,TAU);c.fill();c.restore();
    }
    const amp=reduceMotion.matches?0:(S.vib<0.1?0.15:0.3+3.0*S.vib);
    const sx=amp*(Math.random()-0.5)*2, sy=amp*(0.6*Math.sin(l.t*97)+0.4*(Math.random()-0.5)*2);
    c.save();c.translate(sx,sy);
    drawVortices(l,false);
    drawBubbles(l);
    drawRings(l);
    if(!drawGeoLU(l)) drawLowerUnit(l);
    if(drawGeoProp(l)){drawVortices(l,true);c.restore();return;}
    const blades=l.blades, conv=l.kind==='conv';
    const ghosts=S.rot>0.25?3:0, spread=0.11*S.rot*(3/blades);
    const order=[];
    for(let i=0;i<blades;i++) order.push(l.rot+i*TAU/blades);
    const draw=(th,a)=>conv?drawConvBlade(l,th,a):drawLoopBlade(l,th,a);
    const pass=(front)=>{
      for(const th of order){
        const z=Math.cos(th);
        if((z>=0)!==front) continue;
        for(let g=ghosts;g>=1;g--) draw(th-g*spread,0.16*(1-g/(ghosts+1)));
        draw(th,front?1:0.85);
      }
    };
    pass(false);drawHub(l);pass(true);
    drawVortices(l,true);
    c.restore();
  }

  /* ---------- vibration trace (seismograph strip) ---------- */
  function drawWave(l,dt){
    const c=l.wctx;
    if(!c||!l.ww) return;
    const {ww:w,wh:h,S}=l, conv=l.kind==='conv';
    const add=Math.max(1,Math.round(dt*120));
    for(let i=0;i<add;i++){
      l.wph+=0.55+1.1*S.rot;
      const A=0.03+0.9*S.vib;
      let v=A*(0.55*Math.sin(l.wph)+0.28*Math.sin(2.7*l.wph+1.3)+(Math.random()-0.5)*0.9*S.cav);
      if(Math.random()<0.05*S.cav) v+=(Math.random()<0.5?-1:1)*A*0.8;
      if(!conv) v=v*0.6+(Math.random()-0.5)*0.015;
      l.wave.push(clamp(v,-1,1));
    }
    while(l.wave.length>Math.ceil(w/2)) l.wave.shift();
    c.clearRect(0,0,w,h);
    c.strokeStyle='rgba(255,255,255,0.08)';c.lineWidth=1;
    c.beginPath();c.moveTo(0,h/2);c.lineTo(w,h/2);c.stroke();
    const col=conv?'255,179,107':'142,227,255';
    const n=l.wave.length, x0=w-n*2;
    const path=()=>{c.beginPath();for(let i=0;i<n;i++){const x=x0+i*2,y=h/2-l.wave[i]*(h/2-3);i?c.lineTo(x,y):c.moveTo(x,y);}};
    c.lineJoin='round';
    path();c.strokeStyle='rgba('+col+',0.18)';c.lineWidth=4;c.stroke();
    path();c.strokeStyle='rgba('+col+',0.95)';c.lineWidth=1.4;c.stroke();
  }

  function setMeters(l){
    const S=l.S, j=()=>(Math.random()*0.06-0.03)*Math.min(1,S.vib*2+0.2);
    const v=l.kind==='conv'?{vortex:clamp(S.cav*0.9+S.turb*0.15+j(),0,1),cav:clamp(S.cav+j(),0,1),vib:clamp(S.vib+j()*2,0,1)}
                          :{vortex:clamp(0.02+0.06*S.rot,0,1),cav:clamp(S.cav+j()*0.3,0,1),vib:clamp(S.vib+j()*0.3,0,1)};
    for(const k in v){if(l.meters[k]) l.meters[k].style.width=(v[k]*100).toFixed(1)+'%';}
  }

  function stepLane(l,dt){if(!l.w) return;stepFlow(l,dt);stepBubbles(l,dt);}
  function renderLane(l,dt){if(!l.w) return;drawBackground(l,dt);drawHardware(l);drawWave(l,dt||1/30);}

  /* ---------- simulated sound (Web Audio) ----------
     Every layer is tied to a physical quantity from the test data, heard from the helm:
       engine     combustion pulses at the firing frequency (RPM/60 x cylinders/2), muffled because outboards
                  exhaust through the prop hub; one slightly detuned voice per engine (twin, triple...)
       gearcase   pinion gear-mesh whine (crank RPM/60 x 14 teeth)
       blade rate prop RPM/60 x blade count: the hum you feel as vibration (both props)
       cavitation as heard above water: tones at 1x, 2x, 4x and 8x blade rate, each at an amplitude equal to
                  the tip-cavitation magnitude. SHARROW barely cavitates at the tips, so it is blade rate only
       water      rush and hull slap scaled by the measured boat speed; wind above ~15 mph
     A short hull resonance and reverb place it on the boat. It is a simulation, not a recording. */
  function engineSpec(str){
    const s=str||'';
    const n=/quint/i.test(s)?5:/quad/i.test(s)?4:/triple/i.test(s)?3:/twin/i.test(s)?2:1;
    let cyl=8,diesel=false,sc=false;
    if(/V10/i.test(s)) cyl=10;
    else if(/cox/i.test(s)){cyl=8;diesel=true;}
    else if(/diesel/i.test(s)){cyl=4;diesel=true;}
    else if(/yamaha\s*300/i.test(s)) cyl=6;
    else if(/(yamaha|suzuki)\s*200/i.test(s)) cyl=4;
    if(/450R/i.test(s)) sc=true;
    return {n,cyl,diesel,sc};
  }
  function buildSynth(ctx,dest){
    const sr=ctx.sampleRate;
    const now=()=>ctx.currentTime;
    let instant=false;
    const set=(param,v,tc)=>{if(instant) param.setValueAtTime(v,now());else param.setTargetAtTime(v,now(),tc||0.08);};
    const G=v=>{const g=ctx.createGain();g.gain.value=v;return g;};
    const F=(type,f,q)=>{const b=ctx.createBiquadFilter();b.type=type;b.frequency.value=f;if(q!=null) b.Q.value=q;return b;};
    const buf=(sec,fill)=>{const b=ctx.createBuffer(1,Math.floor(sr*sec),sr);fill(b.getChannelData(0));return b;};
    const loop=b=>{const s=ctx.createBufferSource();s.buffer=b;s.loop=true;return s;};
    const sources=[];

    /* noise colours */
    const white=buf(2,d=>{for(let i=0;i<d.length;i++) d[i]=Math.random()*2-1;});
    const pink=buf(3,d=>{let b0=0,b1=0,b2=0,b3=0,b4=0,b5=0,b6=0;for(let i=0;i<d.length;i++){const w=Math.random()*2-1;
      b0=0.99886*b0+w*0.0555179;b1=0.99332*b1+w*0.0750759;b2=0.969*b2+w*0.153852;b3=0.8665*b3+w*0.3104856;b4=0.55*b4+w*0.5329522;b5=-0.7616*b5-w*0.016898;
      d[i]=(b0+b1+b2+b3+b4+b5+b6+w*0.5362)*0.11;b6=w*0.115926;}});
    const brown=buf(3,d=>{let l=0;for(let i=0;i<d.length;i++){l=(l+0.02*(Math.random()*2-1))/1.02;d[i]=l*3.5;}});
    /* low thumps for hull slap */
    const slap=buf(4,d=>{for(let i=Math.floor(sr*0.1);i<d.length;){const L=Math.floor(sr*(0.05+Math.random()*0.08)),a=0.4+Math.random()*0.6;
      for(let k=0;k<L&&i+k<d.length;k++) d[i+k]+=Math.sin(k/sr*TAU*(55+Math.random()*4))*a*Math.exp(-k/(L*0.22));i+=Math.floor(sr*(0.18+Math.random()*0.55));}});
    /* one click per 10 ms; playbackRate turns it into a pulse train at any rate (diesel knock) */
    const clicks=buf(1,d=>{for(let p=0;p<100;p++){const o=Math.floor(p*sr/100);for(let k=0;k<sr*0.0025&&o+k<d.length;k++) d[o+k]=(Math.random()*2-1)*Math.exp(-k/(sr*0.0006));}});

    /* ---- output chain: hull resonance, helm low-pass, short reverb, compressor ---- */
    const master=G(0);master.connect(dest);
    const comp=ctx.createDynamicsCompressor();comp.threshold.value=-8;comp.knee.value=10;comp.ratio.value=2;comp.attack.value=0.01;comp.release.value=0.2;comp.connect(master);
    const helm=F('lowpass',7200,0.5);helm.connect(comp);
    const hull=F('peaking',115,1.1);hull.gain.value=5;hull.connect(helm);
    const verb=ctx.createConvolver();
    verb.buffer=(()=>{const b=ctx.createBuffer(2,Math.floor(sr*0.45),sr);for(let ch=0;ch<2;ch++){const d=b.getChannelData(ch);let lp=0;for(let i=0;i<d.length;i++){lp+=0.25*((Math.random()*2-1)-lp);d[i]=lp*Math.exp(-i/(sr*0.09));}}return b;})();
    const wet=G(0.22);verb.connect(wet);wet.connect(helm);
    const bus=G(1);bus.connect(hull);bus.connect(verb);

    /* ---- engines ---- */
    const engLP=F('lowpass',300,0.7), engGain=G(0);engLP.connect(engGain);engGain.connect(bus);
    const comb=(()=>{const N=24,re=new Float32Array(N),im=new Float32Array(N);for(let k=1;k<N;k++){im[k]=Math.pow(k,-0.85)*(k%2?1:0.7);re[k]=0.15*Math.pow(k,-1.2);}return ctx.createPeriodicWave(re,im);})();
    const voices=[];
    for(let i=0;i<4;i++){
      const o=ctx.createOscillator();o.setPeriodicWave(comb);
      const sub=ctx.createOscillator();sub.type='sine';
      const crank=ctx.createOscillator();crank.type='triangle';
      const vg=G(0),sg=G(0),cg=G(0);
      const pan=ctx.createStereoPanner?ctx.createStereoPanner():null;
      o.connect(vg);sub.connect(sg);crank.connect(cg);
      [vg,sg,cg].forEach(g=>{if(pan) g.connect(pan);else g.connect(engLP);});
      if(pan){pan.pan.value=[-0.35,0.35,-0.12,0.12][i];pan.connect(engLP);}
      [o,sub,crank].forEach(x=>{x.frequency.value=40;sources.push(x);});
      voices.push({o,sub,crank,vg,sg,cg,detune:[0,0.0055,-0.0045,0.009][i],w:0});
    }
    /* exhaust bubbling through the hub */
    const gurgleSrc=loop(brown),gurgleBP=F('bandpass',160,1.2),gurgle=G(0);gurgleSrc.connect(gurgleBP);gurgleBP.connect(gurgle);gurgle.connect(bus);sources.push(gurgleSrc);
    /* diesel knock */
    const knockSrc=loop(clicks),knockHP=F('highpass',1800,0.7),knock=G(0);knockSrc.connect(knockHP);knockHP.connect(knock);knock.connect(bus);sources.push(knockSrc);
    /* supercharger whine (Mercury 450R) */
    const scO=ctx.createOscillator();scO.type='sine';const scG=G(0);scO.connect(scG);scG.connect(bus);sources.push(scO);
    /* gearcase whine */
    const gearO=ctx.createOscillator();gearO.type='triangle';const gearBP=F('bandpass',700,6),gearG=G(0);gearO.connect(gearBP);gearBP.connect(gearG);gearG.connect(bus);sources.push(gearO);

    /* ---- blade-rate hum (both props) ---- */
    const bO=ctx.createOscillator();bO.type='sine';
    const bAmp=G(0);bO.connect(bAmp);bAmp.connect(bus);
    sources.push(bO);

    /* ---- tip-cavitation tones: 1x, 2x, 4x, 8x blade rate, equal amplitude = cavitation magnitude ---- */
    const CAV_H=[1,2,4,8];
    const cavMix=G(1),cavShape=ctx.createWaveShaper();
    cavShape.curve=(()=>{const n=1024,c=new Float32Array(n);for(let i=0;i<n;i++){const x=i/(n-1)*2-1;c[i]=Math.tanh(2.2*x)/Math.tanh(2.2);}return c;})();
    cavMix.connect(cavShape);cavShape.connect(bus);
    const cavTones=CAV_H.map(h=>{const o=ctx.createOscillator();o.type='sine';const g=G(0);o.connect(g);g.connect(cavMix);sources.push(o);return {h,o,g,j:0};});
    /* slow amplitude flutter: the vapor cores grow and collapse unsteadily */
    const flutSrc=loop(white),flutLP=F('lowpass',6,0.7),flut=G(0);flutSrc.connect(flutLP);flutLP.connect(flut);flut.connect(cavMix.gain);
    sources.push(flutSrc);

    /* ---- water rush, hull slap, wind ---- */
    const wSrc=loop(pink),wBP=F('bandpass',500,0.55),water=G(0);wSrc.connect(wBP);wBP.connect(water);water.connect(bus);
    const sSrc=loop(slap),slapLP=F('lowpass',220,0.7),slapG=G(0);sSrc.connect(slapLP);slapLP.connect(slapG);slapG.connect(bus);
    const windSrc=loop(white),windHP=F('highpass',900,0.5),windLP=F('lowpass',5200,0.5),wind=G(0);windSrc.connect(windHP);windHP.connect(windLP);windLP.connect(wind);wind.connect(bus);
    sources.push(wSrc,sSrc,windSrc);

    sources.forEach(s=>s.start());

    return {
      ctx,
      /* p: {rpm, propRpm, blades, mph, tipCav, vib, cyl, engines, diesel, sc, active} */
      update(p,opts){
        instant=!!(opts&&opts.instant);
        const rpmF=clamp(p.rpm/6000,0,1), sp=clamp(p.mph/60,0,1.2);
        const fFire=p.rpm/60*p.cyl/2, fCrank=p.rpm/60, nv=Math.min(4,p.engines||1);
        set(master.gain,p.active?0.62:0,p.active?0.12:0.06);
        /* engines */
        voices.forEach((v,i)=>{
          const on=i<nv;
          if(!instant) v.w+=((Math.random()-0.5)*0.004-v.w)*0.15;
          const m=1+v.detune+v.w;
          set(v.o.frequency,fFire*m,0.05);set(v.sub.frequency,fFire*m/2,0.05);set(v.crank.frequency,fCrank*m,0.05);
          const vol=on?1/Math.sqrt(nv):0;
          set(v.vg.gain,vol);set(v.sg.gain,vol*(p.cyl>=8?0.3:0.18));set(v.cg.gain,vol*(p.cyl===4?0.22:0.12));
        });
        set(engLP.frequency,170+260*rpmF+fFire*0.9);
        set(engGain.gain,(p.diesel?0.11:0.085)*(0.55+0.7*rpmF),0.12);
        set(gurgleBP.frequency,90+fFire*0.6);set(gurgle.gain,0.05+0.07*rpmF,0.15);
        knockSrc.playbackRate.setTargetAtTime(Math.max(0.05,fFire/100),now(),instant?0.001:0.05);
        set(knock.gain,p.diesel?0.05+0.06*rpmF:0);
        set(scO.frequency,p.rpm/60*2.6*4);set(scG.gain,p.sc?0.004+0.016*rpmF*rpmF:0);
        set(gearO.frequency,p.rpm/60*14);set(gearBP.frequency,p.rpm/60*14);set(gearG.gain,0.006+0.02*rpmF);
        /* blade rate */
        const fB=p.propRpm/60*p.blades;
        set(bO.frequency,fB,0.05);
        set(bAmp.gain,0.02+0.08*p.vib,0.1);
        /* tip cavitation tones (0 for SHARROW) */
        const tc=clamp(p.tipCav||0,0,1), A=0.2*tc;
        cavTones.forEach(t=>{
          if(!instant) t.j+=((Math.random()-0.5)*0.003-t.j)*0.2;
          set(t.o.frequency,fB*t.h*(1+t.j),0.05);set(t.g.gain,A,0.12);
        });
        set(flut.gain,0.35*tc,0.2);
        /* water, slap, wind (driven by the measured boat speed) */
        set(wBP.frequency,320+1100*sp);set(water.gain,0.02+0.2*Math.pow(sp,1.5),0.15);
        sSrc.playbackRate.setTargetAtTime(0.5+1.4*sp,now(),instant?0.001:0.15);set(slapG.gain,0.16*smooth(0.12,0.5,sp),0.2);
        set(wind.gain,0.12*Math.pow(smooth(0.25,1.1,sp),1.4),0.2);
        instant=false;
      }
    };
  }
  window.SharrowFlowSynth={build:buildSynth,engineSpec};
  function makeAudio(){
    const AC=window.AudioContext||window.webkitAudioContext;
    if(!AC) return null;
    const ctx=new AC();
    return buildSynth(ctx,ctx.destination);
  }

  /* ---------- controller ---------- */
  function init(root){
    if(!root||root.dataset.ready==='1') return;
    root.dataset.ready='1';
    const $=s=>root.querySelector(s);
    const slider=$('[data-sflow-throttle]'), out=$('[data-sflow-rpm]');
    const pauseBtn=$('[data-sflow-pause]'), pauseLabel=$('[data-sflow-pause-label]');
    const soundBtn=$('[data-sflow-sound]'), soundLabel=$('[data-sflow-sound-label]');
    const hear=$('[data-sflow-hear]'), listenBtns=[...root.querySelectorAll('[data-sflow-listen]')];
    const select=$('[data-sflow-report]'), reportLink=$('[data-sflow-report-link]'), dataPanel=$('[data-sflow-data]');
    const sf={};root.querySelectorAll('[data-sf]').forEach(e=>{sf[e.dataset.sf]=e;});
    const lanes=[...root.querySelectorAll('[data-sflow-lane]')].map(el=>makeLane(el,el.dataset.sflowLane));
    const laneOf=k=>lanes.find(l=>l.kind===k);
    const reports=Array.isArray(window.SHARROW_FLOW_REPORTS)?window.SHARROW_FLOW_REPORTS:[];
    let report=null, rmin=600, rmax=6000, vmax=50;
    let audio=null, soundOn=false, listen='conv';
    let target=slider.value/100, T=target, userSet=false, paused=reduceMotion.matches, visible=false, raf=0, last=0, uiClock=0;
    let phys={sharrow:null,conv:null}, engFrac=T;
    const realNote=$('[data-sflow-real]');
    const gainBox=$('[data-sflow-gain]'), gainSvg=$('[data-sflow-gain-svg]');
    const slot=$('[data-sflow-slot]'), controls=$('[data-sflow-controls]'), lanesEl=$('.sflow-lanes');
    const sfd={};root.querySelectorAll('[data-sfd]').forEach(e=>{sfd[e.dataset.sfd]=e;});
    const tripEl=$('[data-sflow-trip]'),sft={};root.querySelectorAll('[data-sft]').forEach(e=>{sft[e.dataset.sft]=e;});
    /* 100 statute miles at the measured speeds for this RPM */
    const fmtHM=h=>{if(!isFinite(h)||h>99) return '\u2014';const m=Math.round(h*60);return (m>=60?Math.floor(m/60)+' h ':'')+(m%60)+' min';};
    function updateTrip(vs,vc,rpm){
      if(!tripEl) return;
      if(!(vs>0.5&&vc>0.5)){tripEl.hidden=true;return;}
      tripEl.hidden=false;
      const ts=100/vs,tc=100/vc,pct=(vs/vc-1)*100,max=Math.max(ts,tc);
      if(pct>=0.5){sft.head.textContent='Go 100 miles '+Math.round(pct)+'% faster';sft.sub.textContent='with SHARROW\u2122 at '+fmtRpm(rpm)+' RPM \u00b7 arrive '+fmtHM(tc-ts)+' sooner';}
      else if(pct<=-0.5){sft.head.textContent='Conventional is '+Math.round(-pct)+'% faster here';sft.sub.textContent='over 100 miles at '+fmtRpm(rpm)+' RPM \u00b7 '+fmtHM(ts-tc)+' sooner';}
      else{sft.head.textContent='Same time over 100 miles';sft.sub.textContent='at '+fmtRpm(rpm)+' RPM';}
      sft.ts.textContent=fmtHM(ts);sft.tc.textContent=fmtHM(tc);
      sft.bs.style.width=(ts/max*100).toFixed(1)+'%';sft.bc.style.width=(tc/max*100).toFixed(1)+'%';
    }
    const mqMobile=window.matchMedia('(max-width: 700px)');
    let gainModel=null;

    /* ---- speed-gain chart: SHARROW minus conventional at every 250 RPM, from the test data ---- */
    const speedAt=(prop,r)=>interp(prop.pts,r)*(report.units==='kn'?1.15078:1);
    const fmtG=g=>(g>=0?'+':'\u2212')+Math.abs(g).toFixed(1);
    function buildGain(){
      if(!gainBox||!gainSvg) return;
      if(!report){gainBox.hidden=true;gainModel=null;return;}
      gainBox.hidden=false;
      const S=report.sharrow,C=report.conv,step=250;
      const lo=Math.max(S.pts[0][0],C.pts[0][0]),hi=Math.min(S.pts[S.pts.length-1][0],C.pts[C.pts.length-1][0]);
      const bars=[];for(let r=Math.ceil(Math.max(lo,750)/step)*step;r<=hi;r+=step) bars.push({r,g:speedAt(S,r)-speedAt(C,r)});
      const W=Math.max(280,Math.round(gainSvg.clientWidth||gainBox.clientWidth||600)),H=mqMobile.matches?170:200;
      const padL=mqMobile.matches?34:46,padR=8,padT=22,padB=24,pw=W-padL-padR,ph=H-padT-padB;
      const gMax=Math.max(20,Math.ceil(Math.max(...bars.map(b=>b.g))/5)*5);
      const minG=Math.min(0,...bars.map(b=>b.g)),gMin=minG<0?Math.min(-5,Math.floor(minG/5)*5):0;
      const X=r=>padL+(r-rmin)/(rmax-rmin)*pw, Y=g=>padT+(gMax-g)/(gMax-gMin)*ph;
      const bw=Math.max(3,step/(rmax-rmin)*pw*0.72);
      let peak=bars[0];bars.forEach(b=>{if(b.g>peak.g) peak=b;});
      let o='';
      const m0=Math.max(rmin,2500),m1=Math.min(rmax,4500);
      if(m1>m0){o+='<rect class="sg-mid" x="'+(X(m0)-bw).toFixed(1)+'" y="'+padT+'" width="'+(X(m1)-X(m0)+2*bw).toFixed(1)+'" height="'+ph+'"/>';
        o+='<text class="sg-midt" x="'+((X(m0)+X(m1))/2).toFixed(1)+'" y="'+(padT-8)+'" text-anchor="middle">MID-RANGE</text>';}
      for(let g=gMin;g<=gMax;g+=5){const y=Y(g).toFixed(1);
        o+='<line class="'+(g===0?'sg-zero':'sg-grid')+'" x1="'+padL+'" x2="'+(W-padR)+'" y1="'+y+'" y2="'+y+'"/>';
        if(g%10===0||g===gMin) o+='<text class="sg-yl" x="'+(padL-6)+'" y="'+(+y+3.5).toFixed(1)+'" text-anchor="end">'+(g>0?'+':'')+g+'</text>';}
      bars.forEach((b,i)=>{const y0=Y(0),y1=Y(b.g);
        o+='<rect class="sg-bar'+(b.g<0?' sg-neg':'')+(b===peak?' sg-peak':'')+'" data-i="'+i+'" x="'+(X(b.r)-bw/2).toFixed(1)+'" y="'+Math.min(y0,y1).toFixed(1)+'" width="'+bw.toFixed(1)+'" height="'+Math.max(1,Math.abs(y1-y0)).toFixed(1)+'" rx="'+Math.min(3,bw/3).toFixed(1)+'"/>';});
      o+='<text class="sg-yl" x="'+(padL-6)+'" y="'+(padT-8)+'" text-anchor="end">mph</text>';
      o+='<text class="sg-peakt" x="'+X(peak.r).toFixed(1)+'" y="'+(Y(peak.g)-6).toFixed(1)+'" text-anchor="middle">'+fmtG(peak.g)+'</text>';
      const k0=Math.ceil(rmin/1000)*1000;for(let r=k0;r<=rmax;r+=1000){if(mqMobile.matches&&r%2000&&rmax-rmin>4000) continue;
        o+='<text class="sg-xl" x="'+X(r).toFixed(1)+'" y="'+(H-6)+'" text-anchor="middle">'+(r/1000)+'k</text>';}
      o+='<g class="sg-cur"><line y1="'+(padT-2)+'" y2="'+(padT+ph)+'"/><rect class="sg-curbg" height="18" rx="9"/><text class="sg-curt" text-anchor="middle"></text></g>';
      gainSvg.setAttribute('viewBox','0 0 '+W+' '+H);gainSvg.style.height=H+'px';gainSvg.innerHTML=o;
      gainModel={bars,X,Y,W,H,padL,pw,padT,peak,peakT:gainSvg.querySelector('.sg-peakt'),cur:gainSvg.querySelector('.sg-cur'),rects:[...gainSvg.querySelectorAll('.sg-bar')],last:-1};
      gainSvg.setAttribute('aria-label','Speed gained with SHARROW at each engine RPM. Biggest gain '+fmtG(peak.g)+' mph at '+fmtRpm(peak.r)+' RPM.');
    }
    function updateGainCursor(rpm){
      const m=gainModel;if(!m) return;
      const x=m.X(rpm),line=m.cur.querySelector('line'),t=m.cur.querySelector('text'),bg=m.cur.querySelector('rect');
      line.setAttribute('x1',x.toFixed(1));line.setAttribute('x2',x.toFixed(1));
      let i=-1,best=1e9;m.bars.forEach((b,k)=>{const d=Math.abs(b.r-rpm);if(d<best){best=d;i=k;}});
      if(best>130) i=-1;
      if(i!==m.last){if(m.last>=0) m.rects[m.last].classList.remove('sg-on');if(i>=0) m.rects[i].classList.add('sg-on');m.last=i;}
      const g=speedAt(report.sharrow,rpm)-speedAt(report.conv,rpm);
      if(m.peakT) m.peakT.style.opacity=Math.abs(m.X(m.peak.r)-x)<70?'0':'1';
      t.textContent=fmtG(g)+' mph @ '+fmtRpm(rpm);
      const tw=t.textContent.length*6.4+16,tx=clamp(x,m.padL+tw/2,m.W-tw/2-2),ty=m.padT+12;
      t.setAttribute('x',tx.toFixed(1));t.setAttribute('y',(ty+4).toFixed(1));
      bg.setAttribute('x',(tx-tw/2).toFixed(1));bg.setAttribute('y',(ty-9).toFixed(1));bg.setAttribute('width',tw.toFixed(1));
    }
    if(gainSvg){
      let dragging=false;
      const setFromX=e=>{if(!gainModel) return;const rc=gainSvg.getBoundingClientRect();const vx=(e.clientX-rc.left)/rc.width*gainModel.W;
        const r=rmin+(vx-gainModel.padL)/gainModel.pw*(rmax-rmin);userSet=true;target=clamp((r-rmin)/(rmax-rmin),0,1);slider.value=String(Math.round(target*100));
        if(paused){T=target;redrawStatic();}};
      gainSvg.addEventListener('pointerdown',e=>{dragging=true;try{gainSvg.setPointerCapture(e.pointerId);}catch(err){}setFromX(e);});
      gainSvg.addEventListener('pointermove',e=>{if(dragging) setFromX(e);});
      const end=()=>{dragging=false;};gainSvg.addEventListener('pointerup',end);gainSvg.addEventListener('pointercancel',end);
    }

    /* ---- mobile: dock the throttle at the bottom of the screen while the propellers are in view ---- */
    function updateDock(){
      if(!slot||!controls) return;
      const want=mqMobile.matches&&!!report&&(()=>{const sr=slot.getBoundingClientRect(),lr=(view==='above'&&aboveEl?aboveEl:lanesEl).getBoundingClientRect();return sr.top<4&&lr.bottom>window.innerHeight*0.5;})();
      if(want===root.classList.contains('sflow-docked')) return;
      if(want) slot.style.minHeight=controls.offsetHeight+'px'; else slot.style.minHeight='';
      root.classList.toggle('sflow-docked',want);
    }
    window.addEventListener('scroll',updateDock,{passive:true});window.addEventListener('resize',updateDock);
    /* ---------- above-water view: an overhead "drone" shot of the two boats racing at the same RPM ----------
       Each boat moves at its measured speed for the selected RPM. The camera follows the boat that is behind,
       so the gap you see opening is exactly (SHARROW speed - conventional speed) x time. */
    const aboveEl=$('[data-sflow-above]'), aboveCv=aboveEl?aboveEl.querySelector('canvas'):null, actx=aboveCv?aboveCv.getContext('2d'):null;
    const sfa={};if(aboveEl) aboveEl.querySelectorAll('[data-sfa]').forEach(e=>{sfa[e.dataset.sfa]=e;});
    const viewBtns=[...root.querySelectorAll('[data-sflow-view]')];
    let view=root.dataset.sflowViewDefault==='below'?'below':'above';
    const FT=1.46667; /* ft/s per mph */
    const A={w:0,h:0,dpr:1,scale:3,len:30,runT:0,hold:0,sx:0,cx:0,rpm0:-1,wake:{s:[],c:[]},tile:null,shed:0,t:0,done:false};
    function boatLenFt(name){
      const m=String(name||'').match(/(\d+(?:\.\d+)?)\s*(m\b|′|')?/g)||[];
      for(const tok of m){const n=parseFloat(tok),isM=/m\b/.test(tok);
        if(isM&&n>=6&&n<=18) return n*3.28;
        if(n>=16&&n<=60) return n;
        if(n>=160&&n<=600) return n/10;}
      return 30;
    }
    function buildTile(){
      const t=document.createElement('canvas'),W=320,H=320;t.width=W;t.height=H;const c=t.getContext('2d');
      c.strokeStyle='rgba(190,230,255,0.10)';c.lineWidth=1.2;c.lineCap='round';
      for(let i=0;i<150;i++){const x=Math.random()*W,y=Math.random()*H,l=6+Math.random()*22,b=(Math.random()-0.5)*4;
        for(const dx of [0,-W,W]) for(const dy of [0,-H,H]){c.beginPath();c.moveTo(x+dx,y+dy);c.quadraticCurveTo(x+dx+l/2,y+dy+b,x+dx+l,y+dy);c.stroke();}}
      c.fillStyle='rgba(255,255,255,0.05)';
      for(let i=0;i<70;i++){c.beginPath();c.arc(Math.random()*W,Math.random()*H,0.6+Math.random()*1.2,0,TAU);c.fill();}
      return t;
    }
    function sizeAbove(){
      if(!aboveCv) return;
      const w=aboveCv.clientWidth,h=aboveCv.clientHeight;if(!w||!h) return;
      A.dpr=Math.min(2,window.devicePixelRatio||1);A.w=w;A.h=h;
      aboveCv.width=Math.round(w*A.dpr);aboveCv.height=Math.round(h*A.dpr);
      if(!A.tile) A.tile=buildTile();
      A.len=boatLenFt(report&&report.name);
      const viewFt=Math.max(w<560?170:230,A.len*(w<560?5.2:7));
      A.scale=w/viewFt;
    }
    function aboveSpeeds(){
      if(report&&phys.sharrow) return {vs:phys.sharrow.v,vc:phys.conv.v,rpm:rmin+T*(rmax-rmin)};
      const base=4+T*50;return {vs:base,vc:base*(1-0.3*Math.exp(-Math.pow((T-0.4)/0.15,2))),rpm:600+T*5400};
    }
    function resetRun(){A.runT=0;A.hold=0;A.sx=0;A.cx=0;A.wake.s=[];A.wake.c=[];A.done=false;}
    function stepAbove(dt){
      if(!aboveCv||!A.w) return;
      const sp=aboveSpeeds();A.t+=dt;
      if(A.rpm0<0||Math.abs(sp.rpm-A.rpm0)>150){A.rpm0=sp.rpm;resetRun();}
      if(A.hold>0){A.hold-=dt;if(A.hold<=0){A.rpm0=sp.rpm;resetRun();}else return;}
      A.runT+=dt;A.sx+=sp.vs*FT*dt;A.cx+=sp.vc*FT*dt;
      const lead=Math.abs(A.sx-A.cx)*A.scale;
      if(lead>A.w*0.58||A.runT>=15){A.hold=2.8;A.done=true;}
      A.shed+=dt;
      if(A.shed>0.045){A.shed=0;
        A.wake.s.push({x:A.sx-A.len,age:0});A.wake.c.push({x:A.cx-A.len,age:0});}
      for(const k of ['s','c']){const W=A.wake[k];for(const p of W) p.age+=dt;while(W.length&&W[0].age>4) W.shift();}
    }
    function drawBoat(c,x,y,L,B,engines,accent,mph){
      /* top-down hull, bow pointing right at (x,y) */
      c.save();c.translate(x,y);
      c.fillStyle='rgba(0,10,25,0.35)';c.beginPath();c.ellipse(-L*0.48+3,4,L*0.52,B*0.5,0,0,TAU);c.fill();
      const hull=()=>{c.beginPath();c.moveTo(0,0);c.bezierCurveTo(-L*0.12,-B*0.42,-L*0.38,-B*0.5,-L*0.55,-B*0.5);c.lineTo(-L*0.97,-B*0.47);
        c.quadraticCurveTo(-L,-B*0.46,-L,-B*0.38);c.lineTo(-L,B*0.38);c.quadraticCurveTo(-L,B*0.46,-L*0.97,B*0.47);c.lineTo(-L*0.55,B*0.5);c.bezierCurveTo(-L*0.38,B*0.5,-L*0.12,B*0.42,0,0);c.closePath();};
      const g=c.createLinearGradient(0,-B/2,0,B/2);g.addColorStop(0,'#ffffff');g.addColorStop(0.55,'#eef3f6');g.addColorStop(1,'#c9d3da');
      hull();c.fillStyle=g;c.fill();c.lineWidth=1;c.strokeStyle='rgba(20,40,60,0.45)';c.stroke();
      /* accent stripe along the gunwale */
      c.save();hull();c.clip();c.strokeStyle=accent;c.lineWidth=Math.max(1.5,B*0.07);hull();c.stroke();c.restore();
      /* deck */
      c.fillStyle='#dfe7ec';c.beginPath();c.moveTo(-L*0.1,0);c.bezierCurveTo(-L*0.18,-B*0.3,-L*0.4,-B*0.38,-L*0.55,-B*0.38);c.lineTo(-L*0.93,-B*0.37);c.lineTo(-L*0.93,B*0.37);c.lineTo(-L*0.55,B*0.38);c.bezierCurveTo(-L*0.4,B*0.38,-L*0.18,B*0.3,-L*0.1,0);c.fill();
      /* T-top and console */
      c.fillStyle='rgba(30,40,52,0.88)';const tx=-L*0.66,tw=L*0.24,th=B*0.62;c.beginPath();c.roundRect?c.roundRect(tx,-th/2,tw,th,3):c.rect(tx,-th/2,tw,th);c.fill();
      c.fillStyle='rgba(140,200,235,0.55)';c.fillRect(tx+tw*0.82,-th*0.36,tw*0.12,th*0.72);
      c.fillStyle='rgba(255,255,255,0.18)';c.fillRect(tx+2,-th/2+2,tw-4,th*0.18);
      /* bow cushion + leaning post */
      c.fillStyle='rgba(120,135,150,0.45)';c.beginPath();c.ellipse(-L*0.25,0,L*0.08,B*0.22,0,0,TAU);c.fill();
      /* outboards on the transom */
      const n=Math.max(1,Math.min(5,engines)),sp=B*0.82/n;
      for(let i=0;i<n;i++){const ey=-B*0.41+sp*(i+0.5);
        c.fillStyle='#1d242b';c.beginPath();c.roundRect?c.roundRect(-L-L*0.075,ey-sp*0.33,L*0.085,sp*0.66,3):c.rect(-L-L*0.075,ey-sp*0.33,L*0.085,sp*0.66);c.fill();
        c.fillStyle='rgba(255,255,255,0.25)';c.fillRect(-L-L*0.07,ey-sp*0.3,L*0.03,sp*0.6);}
      /* bow spray */
      const sprayA=clamp((mph-12)/40,0,0.7);
      if(sprayA>0){c.strokeStyle='rgba(255,255,255,'+sprayA.toFixed(2)+')';c.lineWidth=2;
        for(const s of [-1,1]){c.beginPath();c.moveTo(-L*0.08,s*B*0.2);c.quadraticCurveTo(-L*0.22,s*B*0.62,-L*0.42,s*B*0.75);c.stroke();}}
      c.restore();
    }
    function drawWake(c,pts,bowX,y,L,B,mph,camX){
      if(pts.length<2) return;
      const s=A.scale, stern=bowX-L, tanK=0.36, a0=clamp(mph/45,0.15,0.75);
      const sternW=(A.sx+0); /* unused */
      for(const side of [-1,1]){
        c.beginPath();let started=false;
        for(let i=pts.length-1;i>=0;i--){const p=pts[i],sx=(p.x-camX)*s;const d=Math.max(0,stern-sx);
          const yy=y+side*(B*0.45+d*tanK);if(sx<-40) break;if(!started){c.moveTo(stern,y+side*B*0.45);started=true;}c.lineTo(sx,yy);}
        const g=c.createLinearGradient(stern,0,stern-A.w*0.7,0);g.addColorStop(0,'rgba(255,255,255,'+a0+')');g.addColorStop(1,'rgba(255,255,255,0)');
        c.strokeStyle=g;c.lineWidth=2.2;c.stroke();
      }
      /* churned prop wash straight behind the transom */
      const wl=Math.min(A.w*0.45,L*(0.8+mph/22));
      const wg=c.createLinearGradient(stern,0,stern-wl,0);wg.addColorStop(0,'rgba(240,250,255,'+(0.35*a0+0.1).toFixed(2)+')');wg.addColorStop(1,'rgba(240,250,255,0)');
      c.fillStyle=wg;c.beginPath();c.moveTo(stern,y-B*0.3);c.quadraticCurveTo(stern-wl*0.5,y-B*0.36,stern-wl,y-B*0.2);c.lineTo(stern-wl,y+B*0.2);c.quadraticCurveTo(stern-wl*0.5,y+B*0.36,stern,y+B*0.3);c.closePath();c.fill();
      c.fillStyle='rgba(255,255,255,0.35)';
      for(let i=0;i<14;i++){const f=((i*97.13+A.t*mph*2.2)%wl);const yy=y+Math.sin(i*12.9+A.t*3)*B*0.28*(1+f/wl);c.beginPath();c.arc(stern-f,yy,1+1.6*(1-f/wl),0,TAU);c.fill();}
    }
    function renderAbove(){
      if(!actx||!A.w) return;
      const c=actx,w=A.w,h=A.h,s=A.scale;c.setTransform(A.dpr,0,0,A.dpr,0,0);
      const sp=aboveSpeeds();
      const behind=Math.min(A.sx,A.cx),camX=behind-(w*0.26)/s;
      /* water */
      const bg=c.createLinearGradient(0,0,0,h);bg.addColorStop(0,'#0b4a73');bg.addColorStop(0.5,'#08405f');bg.addColorStop(1,'#063450');
      c.fillStyle=bg;c.fillRect(0,0,w,h);
      if(A.tile){const pat=c.createPattern(A.tile,'repeat');const off=((camX*s)%320+320)%320;
        c.save();c.translate(-off,0);c.fillStyle=pat;c.fillRect(off,0,w+320,h);c.restore();}
      const yS=h*0.32,yC=h*0.72,L=clamp(A.len*s,34,190),B=L*0.34;
      /* distance ruler along the bottom: every 50 ft from the start line */
      c.fillStyle='rgba(255,255,255,0.4)';c.font='700 10px Inter,Arial,sans-serif';c.textAlign='center';
      const ft0=Math.ceil(camX/50)*50;
      for(let f=ft0;(f-camX)*s<w;f+=50){const x=(f-camX)*s;c.fillRect(x,h-14,1,f%100===0?8:5);if(f%100===0&&f>0) c.fillText(f+' ft',x,h-18);}
      /* start line */
      const x0=(0-camX)*s;
      if(x0>-20&&x0<w+20){c.strokeStyle='rgba(255,255,255,0.55)';c.setLineDash([6,6]);c.lineWidth=2;c.beginPath();c.moveTo(x0,8);c.lineTo(x0,h-24);c.stroke();c.setLineDash([]);
        for(const yy of [h*0.12,h*0.52,h*0.9]){c.fillStyle='#ff8a3c';c.beginPath();c.arc(x0,yy,5,0,TAU);c.fill();}
        c.save();c.translate(x0-8,h*0.5);c.rotate(-Math.PI/2);c.fillStyle='rgba(255,255,255,0.7)';c.font='900 10px Inter,Arial,sans-serif';c.fillText('START',0,0);c.restore();}
      const bowS=(A.sx-camX)*s,bowC=(A.cx-camX)*s;
      const eng=report?engineSpec(report.engine).n:2;
      drawWake(c,A.wake.s,bowS,yS,L,B,sp.vs,camX);drawWake(c,A.wake.c,bowC,yC,L,B,sp.vc,camX);
      drawBoat(c,bowS,yS,L,B,eng,'#00a8e8',sp.vs);drawBoat(c,bowC,yC,L,B,eng,'#ff9a4a',sp.vc);
      /* lane labels follow the boats */
      c.textAlign='left';c.font='900 11px Inter,Arial,sans-serif';
      const lab=(t,x,y,col)=>{const tw=c.measureText(t).width+16;c.fillStyle='rgba(4,13,26,0.75)';c.beginPath();c.roundRect?c.roundRect(x,y,tw,20,10):c.rect(x,y,tw,20);c.fill();c.fillStyle=col;c.fillText(t,x+8,y+14);};
      lab('SHARROW™ · '+sp.vs.toFixed(1)+' mph',clamp(bowS-L,6,w-170),yS-B/2-28,'#8ee3ff');
      lab('Conventional · '+sp.vc.toFixed(1)+' mph',clamp(bowC-L,6,w-190),yC+B/2+10,'#ffb36b');
      /* lead bracket between the bows */
      const dft=A.sx-A.cx;
      if(Math.abs(dft)>1){const xa=Math.min(bowS,bowC),xb=Math.max(bowS,bowC),ym=(yS+yC)/2;
        c.strokeStyle='rgba(255,255,255,0.55)';c.lineWidth=1;c.setLineDash([3,4]);
        c.beginPath();c.moveTo(bowS,yS+B*0.2);c.lineTo(bowS,ym+8);c.moveTo(bowC,yC-B*0.2);c.lineTo(bowC,ym-8);c.stroke();c.setLineDash([]);
        c.strokeStyle='#fff';c.lineWidth=2;c.beginPath();c.moveTo(xa,ym);c.lineTo(xb,ym);
        c.moveTo(xb-6,ym-5);c.lineTo(xb,ym);c.lineTo(xb-6,ym+5);c.stroke();
        const t=(dft>=0?'+':'\u2212')+Math.abs(dft).toFixed(0)+' ft';c.font='900 14px Inter,Arial,sans-serif';c.textAlign='center';
        const tw=c.measureText(t).width+18,tx=clamp((xa+xb)/2,tw/2+4,w-tw/2-4);
        c.fillStyle=dft>=0?'#8ee3ff':'#ffb36b';c.beginPath();c.roundRect?c.roundRect(tx-tw/2,ym-12,tw,24,12):c.rect(tx-tw/2,ym-12,tw,24);c.fill();
        c.fillStyle='#04121f';c.fillText(t,tx,ym+5);}
      /* HUD text */
      if(sfa.lead){
        const sec=A.runT, lengths=Math.abs(dft)/A.len;
        if(Math.abs(dft)<1){sfa.lead.textContent='Same RPM. Same start.';sfa.sub.textContent='Watch the gap open at '+fmtRpm(sp.rpm)+' RPM.';}
        else if(dft>0){sfa.lead.textContent='SHARROW™ is '+Math.round(dft)+' ft ahead';
          sfa.sub.textContent='after '+sec.toFixed(1)+' s at '+fmtRpm(sp.rpm)+' RPM · '+(lengths>=1?lengths.toFixed(1)+' boat lengths':'')+(lengths>=1?' · ':'')+'+'+(sp.vs-sp.vc).toFixed(1)+' mph';}
        else{sfa.lead.textContent='Conventional is '+Math.round(-dft)+' ft ahead';sfa.sub.textContent='after '+sec.toFixed(1)+' s at '+fmtRpm(sp.rpm)+' RPM · '+(sp.vs-sp.vc).toFixed(1)+' mph';}
        aboveEl.classList.toggle('is-done',A.done);
      }
    }
    function setView(v){
      view=v;root.dataset.view=v;
      viewBtns.forEach(b=>b.setAttribute('aria-pressed',String(b.dataset.sflowView===v)));
      if(aboveEl) aboveEl.hidden=v!=='above';
      if(lanesEl) lanesEl.hidden=v==='above';
      if(v==='above'){sizeAbove();if(paused&&!A.runT){A.rpm0=-1;stepAbove(0);for(let i=0;i<450;i++){stepAbove(1/30);if(A.done) break;}}renderAbove();}
      else{lanes.forEach(l=>l.size());redrawStatic();}
      updateDock();
    }
    viewBtns.forEach(b=>b.addEventListener('click',()=>setView(b.dataset.sflowView)));


    if(select&&reports.length){
      reports.forEach(r=>{const o=document.createElement('option');o.value=r.id;o.textContent=r.name+' · '+r.engine;select.appendChild(o);});
    }else if(select){select.closest('.sflow-loader').hidden=true;}

    const fmtRpm=r=>(Math.round(r/50)*50).toLocaleString('en-US');

    function computeStates(){
      if(!report){
        const sh=laneOf('sharrow').S, cv=laneOf('conv').S;
        cv.rot=T;cv.flow=T;cv.cav=smooth(0.12,0.55,T);cv.vib=clamp(0.12+0.8*T,0,1);cv.turb=T;cv.tipSnd=smooth(0.08,0.35,T);
        laneOf('conv').J=0.9-0.35*Math.exp(-Math.pow((T-0.4)/0.18,2));laneOf('sharrow').J=0.9;
        sh.rot=T;sh.flow=T;sh.cav=clamp(Math.max(0,T-0.72)*0.35,0,1);sh.vib=clamp(0.04+0.12*T,0,1);sh.turb=0.15+0.2*T;
        engFrac=T;phys={sharrow:null,conv:null};
        return;
      }
      const rpm=rmin+T*(rmax-rmin);
      engFrac=rpm/rmax;
      for(const k of ['sharrow','conv']){
        const P=propPhysics(report,report[k],rpm);phys[k]=P;
        const S=laneOf(k).S;
        S.rot=P.rpm/rmax;
        S.flow=clamp(P.v/vmax,0,1);
        const I=smooth(0.7,2.6,P.index);
        laneOf(k).J=P.n>0?(P.v*0.44704*(1-PHYS.wake))/((P.n/60)*report[k].d*0.0254):0.8;
        const load=clamp(P.slip*1.6,0,1)*clamp(P.rpm/2500,0,1);
        if(k==='conv'){
          S.cav=I;
          S.tipSnd=smooth(0.5,1.6,P.index);
          S.vib=clamp(0.06+0.55*I+0.45*load,0,1);
          S.turb=clamp(P.slip*1.3+0.25*I,0,1);
        }else{
          /* closed-loop blades have no free tip, so no tip vortex is shed; only loading-driven cavitation remains */
          S.cav=I*0.35;
          S.vib=clamp(0.03+0.2*I+0.18*load,0,1);
          S.turb=clamp(P.slip*0.7,0,1);
        }
      }
    }

    function showThrottle(){
      const pct=(T*100).toFixed(1)+'%';
      slider.style.setProperty('--sf-fill',pct);
      let txt;
      if(report) txt=fmtRpm(rmin+T*(rmax-rmin))+' RPM';
      else txt=fmtRpm(600+T*5400)+' RPM';
      out.textContent=txt;slider.setAttribute('aria-valuetext',txt);
    }

    function updateReadouts(){
      if(!report){lanes.forEach(l=>{if(l.stat) l.stat.textContent='';});if(tripEl) tripEl.hidden=true;return;}
      const s=phys.sharrow,c=phys.conv;
      const rpm=rmin+T*(rmax-rmin);
      sf.rpm.textContent=fmtRpm(rpm);
      sf.proprpm.textContent='Prop '+fmtRpm(rpm/report.gr)+' RPM · '+report.gr.toFixed(2)+':1';
      sf.vs.textContent=s.v.toFixed(1)+' mph';
      sf.vc.textContent=c.v.toFixed(1)+' mph';
      sf.vths.textContent=(s.wot?'WOT · ':'')+'Pitch speed '+s.vth.toFixed(1);
      sf.vthc.textContent=(c.wot?'WOT · ':'')+'Pitch speed '+c.vth.toFixed(1);
      const dv=s.v-c.v, dvp=c.v>0.5?dv/c.v*100:0;
      sf.dv.textContent=(dv>=0?'+':'−')+Math.abs(dv).toFixed(1)+' mph';
      sf.dvp.textContent=(dvp>=0?'+':'−')+Math.abs(dvp).toFixed(0)+'% '+(dv>=0?'faster with SHARROW™':'slower with SHARROW™');
      const ss=Math.round(s.slip*100), sc=Math.round(c.slip*100);
      sf.ss.textContent=ss+'%';sf.sc.textContent=sc+'%';
      const pts=d=>d+(d===1?' pt':' pts');
      sf.slipadv.textContent=sc>ss?(ss>=2&&sc/ss>=1.15?(sc/ss).toFixed(1)+'× more slip on the conventional prop':pts(sc-ss)+' less slip with SHARROW™'):(ss>sc?pts(ss-sc)+' more slip with SHARROW™':'Equal slip');
      const lvl=cavLevel(c.index);
      sf.cav.textContent=CAV_NAMES[lvl];sf.cav.dataset.level=lvl;
      sf.cavnum.textContent='σ '+c.sigma.toFixed(3)+' · tip AoA '+(c.alpha*180/Math.PI).toFixed(1)+'° · index '+c.index.toFixed(2);
      laneOf('sharrow').stat&&(laneOf('sharrow').stat.textContent=s.v.toFixed(1)+' mph · '+ss+'% slip'+(s.wot?' · WOT':''));
      laneOf('conv').stat&&(laneOf('conv').stat.textContent=c.v.toFixed(1)+' mph · '+sc+'% slip'+(c.wot?' · WOT':''));
      updateTrip(s.v,c.v,rpm);
      if(sfd.vs){sfd.vs.textContent=s.v.toFixed(1);sfd.vc.textContent=c.v.toFixed(1);sfd.dv.textContent=fmtG(dv);}
      updateGainCursor(rpm);
    }

    function loadReport(id){
      report=reports.find(r=>r.id===id)||null;
      laneOf('sharrow').blades=report?report.sharrow.blades:3;
      laneOf('conv').blades=report?report.conv.blades:3;
      if(report){
        const all=[report.sharrow,report.conv];
        rmin=Math.max(500,Math.min(...all.map(p=>p.pts[0][0])));
        rmax=Math.max(...all.map(p=>p.pts[p.pts.length-1][0]));
        vmax=Math.max(...all.map(p=>p.pts[p.pts.length-1][1]))*(report.units==='kn'?1.15078:1);
        dataPanel.hidden=false;if(realNote) realNote.hidden=false;
        reportLink.hidden=false;reportLink.href=report.url;
        sf.note.textContent=report.name+' · '+report.engine+'. '+report.sharrow.label+' vs '+report.conv.label+(report.speedSource==='advance'?'. Measured RPM and speeds from the report’s advance-rate test data (advance × RPM ÷ gear ratio)':'. Measured RPM and speeds from the report’s MPH-by-RPM test data')+(report.units==='kn'?' (converted from knots)':'')+
          '; gear ratio '+report.gr.toFixed(2)+':1 '+(report.grSource==='slip'?'(from the report’s slip or advance-rate chart)':'(standard for this engine, assumed)')+
          '. Pitch speed = prop RPM × pitch ÷ 1056; slip = 1 − actual ÷ pitch speed. Tip cavitation is estimated from tip speed, boat speed and blade loading at about 18 in of tip immersion.'+
          ' Hardware is rendered from CAD as representative geometry: a Yamaha gearcase, a SHARROW 15.5 3-loop and a Mercury ECO 16 \u00d7 17 3-blade (4- and 5-blade props are drawn).';
      }else{
        dataPanel.hidden=true;reportLink.hidden=true;if(realNote) realNote.hidden=true;
      }
      lanes.forEach(l=>{l.size();});
      buildGain();
      if(typeof sizeAbove==='function'){sizeAbove();A.rpm0=-1;}
      computeStates();
      lanes.forEach(l=>{for(let i=0;i<70;i++) stepLane(l,1/30);renderLane(l,0);setMeters(l);});
      updateReadouts();showThrottle();
      root.dispatchEvent(new CustomEvent('sflow:report',{bubbles:true,detail:{id:report?report.id:''}}));
    }

    function audioParams(){
      const l=laneOf(listen), S=l.S, active=soundOn&&visible&&!paused&&!document.hidden;
      if(report){
        const P=phys[listen], e=engineSpec(report.engine);
        return {rpm:P.rpm,propRpm:P.n,blades:report[listen].blades,mph:P.v,tipCav:listen==='conv'?(S.tipSnd||0):0,vib:S.vib,cyl:e.cyl,engines:e.n,diesel:e.diesel,sc:e.sc,active};
      }
      const rpm=600+T*5400, base=4+T*50;
      const mph=listen==='conv'?base*(1-0.3*Math.exp(-Math.pow((T-0.4)/0.15,2))):base;
      return {rpm,propRpm:rpm/1.75,blades:3,mph,tipCav:listen==='conv'?(S.tipSnd||0):0,vib:S.vib,cyl:8,engines:2,diesel:false,sc:false,active};
    }
    function syncAudio(){
      if(!audio) return;
      audio.update(audioParams());
    }

    function frame(now){
      raf=0;
      if(!visible||paused||document.hidden) return;
      const dt=Math.min(0.05,last?(now-last)/1000:1/60);last=now;
      T+=(target-T)*Math.min(1,dt*3.5);
      computeStates();
      if(view==='above'){stepAbove(dt);renderAbove();}
      else lanes.forEach(l=>{stepLane(l,dt);renderLane(l,dt);});
      uiClock+=dt;
      if(uiClock>0.1){uiClock=0;if(view!=='above') lanes.forEach(setMeters);updateReadouts();}
      showThrottle();
      syncAudio();
      raf=requestAnimationFrame(frame);
    }
    const start=()=>{if(!raf&&visible&&!paused&&!document.hidden){last=0;raf=requestAnimationFrame(frame);}};
    const stop=()=>{if(raf){cancelAnimationFrame(raf);raf=0;}syncAudio();};
    const redrawStatic=()=>{computeStates();
      if(view==='above'){if(paused){A.rpm0=-1;stepAbove(0);for(let i=0;i<450;i++){stepAbove(1/30);if(A.done) break;}}renderAbove();}
      else lanes.forEach(l=>{for(let i=0;i<45;i++) stepLane(l,1/30);renderLane(l,0);setMeters(l);});
      updateReadouts();showThrottle();};
    root.sflowRedraw=()=>{if(!raf&&view!=='above') lanes.forEach(l=>renderLane(l,0));};
    loadGeo(root);

    const setPaused=v=>{
      paused=v;
      pauseBtn.setAttribute('aria-pressed',String(v));
      pauseLabel.textContent=v?'Play':'Pause';
      v?stop():start();
    };
    pauseBtn.addEventListener('click',()=>setPaused(!paused));

    const setListen=k=>{
      listen=k;
      listenBtns.forEach(b=>b.setAttribute('aria-pressed',String(b.dataset.sflowListen===k)));
      lanes.forEach(l=>l.el.classList.toggle('is-listening',soundOn&&l.kind===k));
      syncAudio();
    };
    soundBtn.addEventListener('click',()=>{
      if(!audio){audio=makeAudio();if(!audio){soundBtn.disabled=true;soundLabel.textContent='Sound unavailable';return;}}
      if(audio.ctx.state==='suspended') audio.ctx.resume();
      soundOn=!soundOn;
      soundBtn.setAttribute('aria-pressed',String(soundOn));
      soundLabel.textContent=soundOn?'Sound on':'Sound off';
      hear.hidden=!soundOn;
      root.classList.toggle('sflow-sound-on',soundOn);
      if(soundOn&&paused) setPaused(false);
      setListen(listen);
    });
    listenBtns.forEach(b=>b.addEventListener('click',()=>setListen(b.dataset.sflowListen)));
    lanes.forEach(l=>l.el.addEventListener('click',e=>{if(soundOn&&!e.target.closest('button')) setListen(l.kind);}));

    if(select) select.addEventListener('change',()=>{loadReport(select.value);if(paused) redrawStatic();});

    slider.addEventListener('input',()=>{
      userSet=true;target=slider.value/100;
      if(paused){T=target;redrawStatic();}
    });

    /* Before the visitor touches the slider, scrolling through the section opens the throttle. */
    let scrollTick=false;
    const onScroll=()=>{
      if(scrollTick||userSet) return;
      scrollTick=true;
      requestAnimationFrame(()=>{
        scrollTick=false;
        if(userSet) return;
        const r=root.getBoundingClientRect(),vh=window.innerHeight||1;
        const prog=clamp((vh-r.top)/(vh+r.height),0,1);
        target=0.18+0.62*smooth(0.12,0.55,prog);
        slider.value=String(Math.round(target*100));
        if(paused){T=target;showThrottle();}
      });
    };
    window.addEventListener('scroll',onScroll,{passive:true});

    if('IntersectionObserver' in window){
      new IntersectionObserver(es=>{visible=es[0].isIntersecting;visible?start():stop();},{rootMargin:'100px 0px'}).observe(root);
    }else{visible=true;}
    document.addEventListener('visibilitychange',()=>{document.hidden?stop():start();});

    if('ResizeObserver' in window){
      let rw=0;
      new ResizeObserver(()=>{const w=root.clientWidth;if(Math.abs(w-rw)>2){rw=w;lanes.forEach(l=>l.size());sizeAbove();buildGain();redrawStatic();updateDock();}}).observe(root);
    }

    onScroll();
    T=target;
    /* Public hook so other sections (the Test Drive library) can load a test. */
    root.sflowLoad=function(id,throttle){
      if(select) select.value=reports.some(r=>r.id===id)?id:'';
      if(typeof throttle==='number'){userSet=true;target=clamp(throttle,0,1);T=target;slider.value=String(Math.round(target*100));}
      loadReport(select?select.value:id);
      if(paused) redrawStatic();
    };
    /* Default to a real test so visitors see actual data first; ?test=<id> deep-links to a specific test. */
    let qTest='';try{qTest=new URLSearchParams(window.location.search).get('test')||'';}catch(e){}
    const def=(qTest&&reports.some(r=>r.id===qTest)?qTest:'')||root.dataset.sflowDefault||(reports[0]&&reports[0].id)||'';
    if(select&&def&&reports.some(r=>r.id===def)) select.value=def;
    loadReport(select?select.value:'');
    let qRpm=0;try{qRpm=parseFloat(new URLSearchParams(window.location.search).get('rpm'))||0;}catch(e){}
    if(report&&qTest===report.id&&qRpm>0){userSet=true;target=clamp((qRpm-rmin)/(rmax-rmin),0,1);T=target;slider.value=String(Math.round(target*100));redrawStatic();}
    setView(view);
    if(paused) setPaused(true); else start();
  }

  function initAll(){document.querySelectorAll('[data-sharrow-flow]').forEach(init);}
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',initAll,{once:true});
  else initAll();
  document.addEventListener('shopify:section:load',e=>{if(e.target) e.target.querySelectorAll('[data-sharrow-flow]').forEach(init);});
})();
